import { useI18n } from '@/hooks/useI18n';
import { CustomerInfo } from "./CustomerInfo";
import { DashboardItinerary } from "@/lib/models/dashboard";

interface QuoteReviewProps {
    itinerary: DashboardItinerary;
    emailSubject: string;
    customMessage: string;
    logoChoice: 'own' | 'agency' | null;
    includePdf: boolean;
    pdfFilename: string | null;
    sharingMethod: 'create_user' | 'existing_user' | 'shared_link';
    linkPassword?: string;
    linkExpiry?: string | null;
    sharedLinkUrl?: string | null;
    contactEmail: string;
    sendEmail: boolean;
}

export const QuoteReview = ({
    itinerary,
    emailSubject,
    customMessage,
    logoChoice,
    includePdf,
    pdfFilename,
    sharingMethod,
    linkPassword,
    linkExpiry,
    sharedLinkUrl,
    sendEmail,
    contactEmail
}: QuoteReviewProps) => {
    const { t } = useI18n();

    const getLogoLabel = () => {
        if (logoChoice === 'own') return t('itinerary.ownLogo');
        if (logoChoice === 'agency') return t('itinerary.agencyLogo');
        return <span className="text-gray-400 italic">{t('common.noLogo')}</span>;
    };

    return (
      <div className="space-y-6">
        <div className="text-center pb-2">
          <h3 className="text-xl font-semibold text-gray-900">
            {t("itinerary.reviewSummary") || "Review Summary"}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {t("itinerary.reviewBeforeSending") ||
              "Please review the details before sending the quote"}
          </p>
        </div>

        <div className="bg-gray-50 p-6 gap-2 rounded-lg grid grid-cols-1 md:grid-cols-2">
          {/* Sharing Method */}
          <div>
            <dt className="text-sm font-medium text-gray-500">
              {t("itinerary.reviewSharingMethod") || "Sharing Method:"}
            </dt>
            <dd className="mt-1 text-sm text-gray-900">
              {sharingMethod === "create_user" &&
                (t("itinerary.reviewCreateUser") || "Client account access")}
              {sharingMethod === "existing_user" &&
                (t("itinerary.reviewExistingUser") || "Client account access")}
              {sharingMethod === "shared_link" &&
                (t("itinerary.reviewSharedLink") || "Public shared link")}
            </dd>
          </div>

          {/* Send Email Status */}
          <div>
            <dt className="text-sm font-medium text-gray-500">
              {t("itinerary.reviewSendEmail") || "Send Email:"}
            </dt>
            <dd className="mt-1 text-sm text-gray-900">
              {sendEmail ? (
                <span className="text-green-600 font-medium">
                  {t("common.yes") || "Yes"}
                </span>
              ) : (
                <span className="text-orange-600 font-medium">
                  {t("itinerary.manualSending") || "Manual sending"}
                </span>
              )}
            </dd>
          </div>

          {/* Email Subject - only if sending email */}
          {sendEmail && (
            <div>
              <dt className="text-sm font-medium text-gray-500">
                {t("itinerary.reviewEmailSubject") || "Subject:"}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">{emailSubject}</dd>
            </div>
          )}

          {/* Custom Message - only if sending email */}
          {sendEmail && (
            <div>
              <dt className="text-sm font-medium text-gray-500">
                {t("itinerary.reviewCustomMessage") || "Message:"}
              </dt>
              <dd className="mt-1 text-sm text-gray-900 line-clamp-3">
                {customMessage || (
                  <span className="text-gray-400 italic">
                    {t("itinerary.noCustomMessage") || "No custom message"}
                  </span>
                )}
              </dd>
            </div>
          )}

          {/* Logo Choice - only if sending email */}
          {sendEmail && (
            <div>
              <dt className="text-sm font-medium text-gray-500">
                {t("itinerary.reviewLogo") || "Logo:"}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">{getLogoLabel()}</dd>
            </div>
          )}

          {/* Include PDF - only if sending email */}
          {sendEmail && (
            <div>
              <dt className="text-sm font-medium text-gray-500">
                {t("itinerary.reviewPdf") || "PDF:"}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {includePdf
                  ? pdfFilename || t("itinerary.pdfWillBeAttached")
                  : t("itinerary.pdfNotIncluded") || "Not included"}
              </dd>
            </div>
          )}

          {/* Shared Link Details */}
          {sharingMethod === "shared_link" && (
            <>
              {linkPassword && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    {t("itinerary.linkPassword") || "Link Password:"}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 font-mono">
                    {linkPassword}
                  </dd>
                </div>
              )}
              {linkExpiry && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    {t("itinerary.linkExpiry") || "Link Expiry:"}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {linkExpiry
                      ? `${linkExpiry} ${t("common.days")}`
                      : t("itinerary.noExpiry") || "No expiry"}
                  </dd>
                </div>
              )}
              <div className="md:col-span-2">
                <dt className="text-sm font-medium text-gray-500">
                  {t("itinerary.sharedLink") || "Shared Link:"}
                </dt>
                <dd className="mt-1 text-sm text-gray-900 font-mono break-all bg-white p-2 rounded border">
                  {sharedLinkUrl || (
                    <span className="text-gray-400 italic">
                      {t("itinerary.linkNotGenerated") ||
                        "Link not generated yet"}
                    </span>
                  )}
                </dd>
              </div>
            </>
          )}
        </div>

        {/* Manual sending info */}
        {!sendEmail && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <div className="text-sm text-blue-900">
                <p className="font-medium mb-1">
                  {t("itinerary.manualSendingTitle") || "Manual sending"}
                </p>
                <p className="text-blue-700">
                  {t("itinerary.manualSendingInfoReview") ||
                    "After clicking 'Share Proposal', the quote will be prepared. You can then copy the shared link or download the PDF to send it manually to the client."}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
};
